// RoomPhotoCapture — multi-photo Room Photo flow (canvas reframe).
//
// Replaces the old RoomScan review-screen flow for the Room Photo tile.
// User taps Room Photo, takes 1-8 wide shots of the room (one per wall is
// the typical case), then taps Continue. The viewer opens immediately as
// a swipeable gallery of blob URLs. AI runs lazily after Continue —
// fire-and-forget POSTs to enumerate-room-items, one per shot — and
// writes detections to room_photo_detections behind the scenes.
//
// State machine:
//   picking          — waiting on the OS file picker (first shot)
//   compressing      — HEIC + JPEG compression for the first shot (brief)
//   between_shots    — thumbnail strip + Add another + Continue
//   compressing_more — additional shot being compressed (brief)
//   viewer           — RoomPhotoViewer is up with the full gallery
//
// Lazy AI vs eager: ScanItem fires AI per-shot in the background (eager).
// Room Photo fires AI only after Continue (lazy) so abandoned sessions
// don't burn Vision + Claude calls. The trade-off: detections land later
// and are silently missing in the worst case (fire-and-forget). Acceptable
// because the viewer never shows them tonight — they're latent evidence
// for the next-session gesture flow.
//
// HARD CAP: 8 photos per session. After 8, "Add another" disables and a
// Pro upgrade nudge surfaces ("Pro plan unlocks higher room photo limits
// for large or complex rooms"). Stripe isn't live yet, so this is
// informational only — no real tier enforcement.
//
// TODO(orphan-sessions): v10's room_photo_canvas branch creates a new
// room_scan_sessions row per POST. Multi-shot capture currently fires
// N POSTs = N session rows, each with a single source_photo_url. The
// ideal is one session row with all photos in source_photo_urls[]
// and detections grouped under a shared session_id. Either:
//   (a) Edge Function: accept session_id in the request body and reuse
//       it if present + skip the session insert; or
//   (b) Edge Function: accept frames_base64[] in the room_photo_canvas
//       branch and loop processFrame internally (mirrors the legacy
//       photo/video/multi_photo branch).
// Both are server-side; deferred to a focused session.
//
// Race note: a user who immediately opens the Room Photos grid tile
// before the background POSTs return may not see the just-captured
// photos (their source_photo_urls aren't written yet). Acceptable per
// the foundation framing.
//
// Props:
//   room               — { id, ... }
//   homeId             — string
//   onClose            — () => void, fires when the viewer closes
//   sourcePreference   — 'camera' | 'library' (default 'camera')

import { useEffect, useRef, useState } from 'react'
import { Camera, Loader, X, Image as ImageIcon } from 'lucide-react'
import { supabase } from '../supabase'
import { convertHeicIfNeeded } from '../utils/convertHeicIfNeeded'
import { stripExif } from '../utils/stripExif'
import { compressImage } from '../imageUtils'
import RoomPhotoViewer from './RoomPhotoViewer'

const MAX_SHOTS = 8
const SPIN_STYLE = `@keyframes roomphotocapture-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`

const C = {
  bg: '#F2EDE4', card: '#FDFAF6', border: '#DDD4C6',
  amber: '#B8721F', amberLight: '#D4902A', brown: '#2C1D14',
  muted: '#9A7B68', danger: '#B03020',
}
const f = 'Georgia, serif'
const fs = 'Calibri, sans-serif'

async function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = (e) => resolve(String(e.target.result).split(',')[1] || '')
    reader.onerror = () => reject(new Error('Failed to read file'))
    reader.readAsDataURL(file)
  })
}

export default function RoomPhotoCapture({ room, homeId, onClose, sourcePreference = 'camera' }) {
  const [state, setState] = useState('picking')
  // shots: [{ localId, blobUrl, file }]. file is the compressed JPEG kept
  // around so we can POST it on Continue (lazy AI).
  const [shots, setShots] = useState([])
  const [moreShotError, setMoreShotError] = useState(null)

  const firstCameraRef = useRef(null)
  const firstLibraryRef = useRef(null)
  const moreCameraRef = useRef(null)
  const moreLibraryRef = useRef(null)
  const autoLaunchedRef = useRef(false)
  const shotIdCounterRef = useRef(0)
  const blobUrlsRef = useRef([]) // accumulator for unmount cleanup

  const nextShotId = () => {
    shotIdCounterRef.current += 1
    return `room-shot-${shotIdCounterRef.current}`
  }

  // Auto-launch the file picker on initial mount.
  useEffect(() => {
    if (state !== 'picking') return
    if (autoLaunchedRef.current) return
    autoLaunchedRef.current = true
    const handle = requestAnimationFrame(() => {
      if (sourcePreference === 'library') firstLibraryRef.current?.click()
      else firstCameraRef.current?.click()
    })
    return () => cancelAnimationFrame(handle)
  }, [state, sourcePreference])

  // Revoke all accumulated blob URLs on unmount so we don't leak memory.
  useEffect(() => {
    return () => {
      for (const url of blobUrlsRef.current) {
        try { URL.revokeObjectURL(url) } catch (_) { /* noop */ }
      }
      blobUrlsRef.current = []
    }
  }, [])

  async function compressAndAddShot(file) {
    let processed
    try {
      const jpeg = await convertHeicIfNeeded(file)
      const stripped = await stripExif(jpeg)
      processed = await compressImage(stripped, 1600, 0.80)
    } catch (err) {
      console.error('[RoomPhotoCapture] preprocess error', err)
      return null
    }
    const blobUrl = URL.createObjectURL(processed)
    blobUrlsRef.current.push(blobUrl)
    const shot = { localId: nextShotId(), blobUrl, file: processed }
    setShots(prev => [...prev, shot])
    return shot
  }

  // First shot — from auto-launched picker. Empty selection means the user
  // dismissed the picker without taking a photo; close back to the parent.
  async function handleFirstShotSelected(e) {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) {
      onClose()
      return
    }

    setState('compressing')
    const shot = await compressAndAddShot(file)
    if (!shot) {
      // Fail soft: close back to the room view rather than wedge in error.
      onClose()
      return
    }
    setState('between_shots')
  }

  // Subsequent shots — from between_shots. Empty selection just returns
  // to between_shots (don't close the whole flow).
  async function handleAdditionalShotSelected(e) {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    if (shots.length >= MAX_SHOTS) return

    setMoreShotError(null)
    setState('compressing_more')
    const shot = await compressAndAddShot(file)
    if (!shot) {
      setMoreShotError("Couldn't read that photo. Please try another.")
    }
    setState('between_shots')
  }

  function removeShot(localId) {
    setShots(prev => {
      const target = prev.find(s => s.localId === localId)
      if (target?.blobUrl) {
        try { URL.revokeObjectURL(target.blobUrl) } catch (_) { /* noop */ }
        blobUrlsRef.current = blobUrlsRef.current.filter(u => u !== target.blobUrl)
      }
      return prev.filter(s => s.localId !== localId)
    })
  }

  // Continue: fire N parallel fire-and-forget POSTs (one per shot) and
  // transition to the viewer immediately. The Edge Function (v10) handles
  // upload + Vision + Claude per shot; detections land in
  // room_photo_detections asynchronously. We do not await; if any POST
  // fails the detections are silently missing — acceptable for tonight.
  function handleContinue() {
    if (shots.length === 0) return
    for (const shot of shots) {
      (async () => {
        try {
          const base64 = await fileToBase64(shot.file)
          const { error } = await supabase.functions.invoke('enumerate-room-items', {
            body: {
              capture_mode: 'room_photo_canvas',
              photo_base64: base64,
              room_id: room?.id,
              home_id: homeId,
              media_type: 'image/jpeg',
            },
          })
          if (error) {
            console.error('[RoomPhotoCapture] background POST error', error)
          }
        } catch (err) {
          console.error('[RoomPhotoCapture] background POST exception', err)
        }
      })()
    }
    setState('viewer')
  }

  // Inputs are always mounted so refs are stable and auto-launch works.
  const inputs = (
    <>
      <input
        ref={firstCameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        style={{ display: 'none' }}
        onChange={handleFirstShotSelected}
      />
      <input
        ref={firstLibraryRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={handleFirstShotSelected}
      />
      <input
        ref={moreCameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        style={{ display: 'none' }}
        onChange={handleAdditionalShotSelected}
      />
      <input
        ref={moreLibraryRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={handleAdditionalShotSelected}
      />
    </>
  )

  if (state === 'viewer') {
    return (
      <>
        {inputs}
        <RoomPhotoViewer
          photoUrls={shots.map(s => s.blobUrl)}
          onClose={onClose}
          alt="Room photo"
          justCaptured
        />
      </>
    )
  }

  if (state === 'picking' || state === 'compressing') {
    return (
      <div
        style={{
          position: 'fixed', inset: 0, zIndex: 1900,
          background: 'rgba(0,0,0,0.85)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexDirection: 'column', gap: 14,
        }}
      >
        <style>{SPIN_STYLE}</style>
        {inputs}
        {state === 'compressing' && (
          <>
            <Loader
              size={48}
              color="#fff"
              strokeWidth={1.5}
              style={{ animation: 'roomphotocapture-spin 1s linear infinite' }}
            />
            <div style={{ color: '#fff', fontFamily: fs, fontSize: 13 }}>
              Opening photo…
            </div>
          </>
        )}
      </div>
    )
  }

  // 'between_shots' (and the brief 'compressing_more' which renders the
  // same layout with the new shot still being added).
  const atCap = shots.length >= MAX_SHOTS
  const isCompressingMore = state === 'compressing_more'

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 1900,
        background: C.bg,
        display: 'flex', flexDirection: 'column',
        fontFamily: f,
      }}
    >
      <style>{SPIN_STYLE}</style>
      {inputs}

      {/* Header */}
      <div
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: 'calc(14px + env(safe-area-inset-top)) 18px 14px',
          borderBottom: `1px solid ${C.border}`,
          background: C.card, flexShrink: 0,
        }}
      >
        <div style={{ fontFamily: f, fontSize: 22, fontWeight: 700, color: C.brown }}>Room Photo</div>
        <button
          onClick={onClose}
          aria-label="Close"
          style={{
            background: 'transparent', border: 'none', cursor: 'pointer',
            color: C.muted, padding: 6, borderRadius: 8, lineHeight: 0,
          }}
        >
          <X size={22} color={C.muted} />
        </button>
      </div>

      {/* Scrollable body */}
      <div style={{ flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
        {/* Counter pill */}
        <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 14 }}>
          <div
            style={{
              background: C.bg, border: `1px solid ${C.border}`,
              borderRadius: 999, padding: '5px 14px',
              fontFamily: fs, fontSize: 12, color: C.brown, fontWeight: 600,
              letterSpacing: 0.4,
            }}
          >
            {shots.length} of {MAX_SHOTS} photos
          </div>
        </div>

        {/* Hint */}
        <div style={{ textAlign: 'center', padding: '20px 24px 8px' }}>
          <div style={{ fontFamily: f, fontSize: 20, fontWeight: 700, color: C.brown, marginBottom: 6 }}>
            Add another wall?
          </div>
          <div style={{ fontFamily: fs, fontSize: 13, color: C.muted, maxWidth: 340, margin: '0 auto', lineHeight: 1.55 }}>
            {atCap
              ? "Max photos reached. Tap Continue to view."
              : "A bedroom usually needs 3-4 wide shots — one per wall. Take more if you have a complex space."}
          </div>
        </div>

        {/* Action buttons */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '4px 18px 0', alignItems: 'center' }}>
          <button
            onClick={() => moreCameraRef.current?.click()}
            disabled={atCap || isCompressingMore}
            style={{
              width: '100%', maxWidth: 320,
              background: (atCap || isCompressingMore) ? C.bg : `linear-gradient(180deg, ${C.amberLight} 0%, ${C.amber} 100%)`,
              color: (atCap || isCompressingMore) ? C.muted : '#fff',
              border: 'none', borderRadius: 10,
              padding: '16px 22px', fontFamily: f, fontSize: 15,
              fontWeight: 700,
              cursor: (atCap || isCompressingMore) ? 'not-allowed' : 'pointer',
              opacity: (atCap || isCompressingMore) ? 0.7 : 1,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 10,
              boxShadow: (atCap || isCompressingMore) ? 'none' : '0 2px 6px rgba(184,114,31,0.25)',
            }}
          >
            {isCompressingMore ? (
              <Loader size={18} color={C.muted} strokeWidth={1.8} style={{ animation: 'roomphotocapture-spin 1s linear infinite' }} />
            ) : (
              <Camera size={20} strokeWidth={2} color={atCap ? C.muted : '#fff'} />
            )}
            {isCompressingMore ? 'Adding…' : 'Add another'}
          </button>
          <button
            onClick={() => moreLibraryRef.current?.click()}
            disabled={atCap || isCompressingMore}
            style={{
              width: '100%', maxWidth: 320,
              background: '#fff', color: C.brown,
              border: `1px solid ${C.border}`, borderRadius: 10,
              padding: '11px 22px', fontFamily: fs, fontSize: 14,
              fontWeight: 600,
              cursor: (atCap || isCompressingMore) ? 'not-allowed' : 'pointer',
              opacity: (atCap || isCompressingMore) ? 0.5 : 1,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            }}
          >
            <ImageIcon size={16} strokeWidth={2} color={C.brown} />
            Add from Camera Roll
          </button>

          {moreShotError && (
            <div style={{ fontFamily: fs, fontSize: 12, color: C.danger, textAlign: 'center', maxWidth: 320 }}>
              {moreShotError}
            </div>
          )}

          {atCap && (
            <div
              style={{
                fontFamily: fs, fontSize: 12, color: C.muted, textAlign: 'center',
                maxWidth: 320, lineHeight: 1.55, paddingTop: 4,
              }}
            >
              Pro plan unlocks higher room photo limits for large or complex rooms.
            </div>
          )}
        </div>

        <div style={{ height: 20 }} />
      </div>

      {/* Bottom: thumbnail strip + Continue */}
      <div
        style={{
          flexShrink: 0,
          borderTop: `1px solid ${C.border}`,
          background: C.card,
          padding: '12px 14px calc(18px + env(safe-area-inset-bottom))',
        }}
      >
        <div
          style={{
            display: 'flex', gap: 8, overflowX: 'auto',
            paddingBottom: 10, WebkitOverflowScrolling: 'touch',
          }}
        >
          {shots.map(s => (
            <div key={s.localId} style={{ position: 'relative', flexShrink: 0 }}>
              <img
                src={s.blobUrl}
                alt=""
                style={{
                  width: 64, height: 64, objectFit: 'cover',
                  borderRadius: 8, background: C.bg,
                }}
              />
              <button
                onClick={() => removeShot(s.localId)}
                aria-label="Remove photo"
                style={{
                  position: 'absolute', top: -6, right: -6,
                  width: 20, height: 20, borderRadius: '50%',
                  background: '#fff', border: `1px solid ${C.border}`,
                  cursor: 'pointer', padding: 0, lineHeight: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                <X size={11} color={C.muted} />
              </button>
            </div>
          ))}
        </div>
        <button
          onClick={handleContinue}
          disabled={shots.length === 0}
          style={{
            width: '100%',
            background: shots.length === 0 ? C.border : `linear-gradient(180deg, ${C.amberLight} 0%, ${C.amber} 100%)`,
            color: '#fff', border: 'none', borderRadius: 10,
            padding: 14, fontFamily: f, fontSize: 15,
            fontWeight: 700, cursor: shots.length === 0 ? 'not-allowed' : 'pointer',
            boxShadow: shots.length === 0 ? 'none' : '0 2px 6px rgba(184,114,31,0.25)',
          }}
        >
          Continue
        </button>
      </div>
    </div>
  )
}
